import { Grid, Box, Typography, Switch, Divider, Paper, styled, Skeleton } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HistoryIcon from '@mui/icons-material/History';
import { CardReuniao } from './CardReuniao';
import { CardComanda } from './CardComanda';
import { IReuniao, StatusReuniaoEnum } from '../../commons/types/crm';
import { useReuniaoActions } from '../../states/reunioes/reuniao.actions';
import { useComandaActions } from '../../states/comandas/comanda.actions';
import { toast } from 'react-toastify';
import { useTranslation } from '../../commons/useTranslasion';

const Dashboard: React.FC = () => {
  const reuniaoActions = useReuniaoActions();
  const comandaActions = useComandaActions();
  const { translate } = useTranslation();

  const [reunioes, setReunioes] = useState<IReuniao[]>([]);
  const [comandasAtivasPorStatus, setComandasAtivasPorStatus] = useState<{ statusNome: string, total: number }[]>([]);
  const [comandasHistoricoPorStatus, setComandasHistoricoPorStatus] = useState<{ statusNome: string, total: number }[]>([]);
  const [totalComandasAtivas, setTotalComandasAtivas] = useState(0);
  const [totalComandasHistorico, setTotalComandasHistorico] = useState(0);
  const [loadingReunioes, setLoadingReunioes] = useState(true);
  const [loadingComandasAtivas, setLoadingComandasAtivas] = useState(true);
  const [loadingComandasHistorico, setLoadingComandasHistorico] = useState(true);
  const [filtroAtivasB2B, setFiltroAtivasB2B] = useState(false);
  const [filtroHistoricoB2B, setFiltroHistoricoB2B] = useState(false);

  // Componente de Switch personalizado com estilo de toggle
  const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 80, // Reduzido de 130 para 80
    height: 34, // Reduzido de 40 para 34
    padding: 0,
    borderRadius: 17,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(46px)', // Ajustado para posicionar a bolinha corretamente no canto direito
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#2A3EB1', // Azul para B2B
          opacity: 1,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: 30, // Reduzido um pouco
      height: 30, // Reduzido um pouco
      borderRadius: 15,
      backgroundColor: '#fff',
    },
    '& .MuiSwitch-track': {
      borderRadius: 17,
      opacity: 1,
      backgroundColor: '#80B3FF', // Azul claro para B2C
      boxSizing: 'border-box',
    },
  }));

  // Componente para mostrar o texto "B2B" ou "B2C" dentro do toggle
  const CustomToggle = ({ checked, onChange }: { checked: boolean; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void }) => {
    return (
      <Box sx={{ position: 'relative' }}>
        <StyledSwitch checked={checked} onChange={onChange} />
        <Typography
          sx={{
            position: 'absolute',
            top: '50%',
            left: checked ? '22%' : '70%', // Ajustado o posicionamento do texto B2B
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            fontWeight: 'bold',
            pointerEvents: 'none',
            fontSize: '11px' // Reduzido o tamanho da fonte
          }}
        >
          {checked ? 'B2B' : 'B2C'}
        </Typography>
      </Box>
    );
  };

  // Função para carregar as comandas ativas com base no filtro B2B/B2C
  const carregarComandasAtivas = (isB2B: boolean) => {
    setLoadingComandasAtivas(true);
    const modeloComercial = isB2B ? 'B2B' : 'B2C';

    comandaActions
      .agrupamentoStatus(false, modeloComercial)
      .then((data: any) => {
        setComandasAtivasPorStatus(data.resultado);

        // Calcula o total somando os valores do agrupamento
        const total = data.resultado.reduce((sum: number, item: any) => sum + item.total, 0);
        setTotalComandasAtivas(total);
      })
      .catch((error) => toast.error(JSON.stringify(error)))
      .finally(() => setLoadingComandasAtivas(false));
  };

  // Função para carregar as comandas históricas com base no filtro B2B/B2C
  const carregarComandasHistorico = (isB2B: boolean) => {
    setLoadingComandasHistorico(true);
    const modeloComercial = isB2B ? 'B2B' : 'B2C';

    comandaActions
      .agrupamentoStatus(true, modeloComercial)
      .then((data: any) => {
        setComandasHistoricoPorStatus(data.resultado);

        const total = data.resultado.reduce((sum: number, item: any) => sum + item.total, 0);
        setTotalComandasHistorico(total);
      })
      .catch((error) => toast.error(JSON.stringify(error)))
      .finally(() => setLoadingComandasHistorico(false));
  };

  // Handlers para os toggle switches
  const handleToggleAtivas = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroAtivasB2B(event.target.checked);
    carregarComandasAtivas(event.target.checked);
  };

  const handleToggleHistorico = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroHistoricoB2B(event.target.checked);
    carregarComandasHistorico(event.target.checked);
  };

  useEffect(() => {
    // Carregar reuniões
    reuniaoActions
      .list()
      .then((resp: any) => setReunioes(resp.resultado))
      .catch((e) => toast.error(e))
      .finally(() => setLoadingReunioes(false));

    // Inicializar com os valores padrão
    carregarComandasAtivas(filtroAtivasB2B);
    carregarComandasHistorico(filtroHistoricoB2B);
  }, []);

  // Uso de useMemo para melhor performance com filtragem de dados
  const reunioesAgrupadas = useMemo(() => {
    return {
      realizadas: reunioes?.filter(
        (reuniao) =>
          reuniao?.status === StatusReuniaoEnum.PENDENTE_APROVACAO ||
          reuniao?.status === StatusReuniaoEnum.APROVADA
      ) || [],

      proximas: reunioes?.filter(
        (reuniao) =>
          reuniao?.status === StatusReuniaoEnum.AGENDADA &&
          new Date(reuniao?.dtPrevista as Date) >= new Date()
      ) || [],

      atrasadas: reunioes?.filter(
        (reuniao) =>
          reuniao?.status === StatusReuniaoEnum.AGENDADA &&
          new Date(reuniao?.dtPrevista as Date) <= new Date()
      ) || []
    };
  }, [reunioes]);

  return (
    <Box
      sx={{
        mr: 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Grid
        container
        spacing={3}
        sx={{
          width: '100%',
          margin: 0,
          boxSizing: 'border-box'
        }}
      >
        <Grid item xs={12} sm={6} md={4}>
          <CardReuniao
            Icon={EventAvailableIcon}
            titulo={translate('ultimasReunioes')}
            reunioesCard={reunioesAgrupadas.realizadas}
            loading={loadingReunioes}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <CardReuniao
            Icon={ListAltRoundedIcon}
            titulo={translate('proximasReunioes')}
            reunioesCard={reunioesAgrupadas.proximas}
            loading={loadingReunioes}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <CardReuniao
            Icon={ReportGmailerrorredRoundedIcon}
            titulo={translate('reunioesAtrasadas')}
            reunioesCard={reunioesAgrupadas.atrasadas}
            loading={loadingReunioes}
          />
        </Grid>

        {/* Seção de comandas ativas */}
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 2 
          }}>
            {/* Título em linha separada no mobile */}
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              mr: 2, 
              mb: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: { sm: 'nowrap' } // Evita quebra de linha em telas maiores
            }}>
              {translate('comandasAtivas')}
            </Typography>
            
            {/* Contador e toggle na mesma linha no mobile */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}>
              {loadingComandasAtivas ? (
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 2 }} />
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: '#EDF4FF',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <BusinessCenterIcon fontSize="small" sx={{ mr: 1, color: '#2A3EB1' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2A3EB1' }}>
                    {`${translate('total')}: ${totalComandasAtivas}`}
                  </Typography>
                </Paper>
              )}

              <CustomToggle checked={filtroAtivasB2B} onChange={handleToggleAtivas} />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {loadingComandasAtivas ? (
              // Skeleton de carregamento para comandas ativas
              [...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-ativa-${index}`}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Skeleton variant="rectangular" height={40} width="60%" sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={60} width="100%" />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Skeleton variant="rectangular" height={30} width="30%" />
                    </Box>
                  </Box>
                </Grid>
              ))
            ) : (
              // Conteúdo real quando carregado
              comandasAtivasPorStatus.map((comandaStatus, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`status-ativa-${index}`}>
                  <CardComanda
                    titulo={comandaStatus.statusNome}
                    totalComandas={comandaStatus.total}
                    linkTo="/assessorados"
                    icon={AssignmentIcon}
                    loading={false}
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Grid>

        {/* Divisor entre as seções */}
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Seção de comandas históricas */}
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 2 
          }}>
            {/* Título em linha separada no mobile */}
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              mr: 2, 
              mb: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: { sm: 'nowrap' } // Evita quebra de linha em telas maiores
            }}>
              {translate('historico')}
            </Typography>

            {/* Contador e toggle na mesma linha no mobile */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}>
              {loadingComandasHistorico ? (
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 2 }} />
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: '#F5F5F5',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <HistoryIcon fontSize="small" sx={{ mr: 1, color: '#757575' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#757575' }}>
                    {`${translate('total')}: ${totalComandasHistorico}`}
                  </Typography>
                </Paper>
              )}

              <CustomToggle checked={filtroHistoricoB2B} onChange={handleToggleHistorico} />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {loadingComandasHistorico ? (
              // Skeleton de carregamento para comandas históricas
              [...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-historico-${index}`}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Skeleton variant="rectangular" height={40} width="60%" sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={60} width="100%" />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Skeleton variant="rectangular" height={30} width="30%" />
                    </Box>
                  </Box>
                </Grid>
              ))
            ) : (
              // Conteúdo real quando carregado
              comandasHistoricoPorStatus.map((comandaStatus, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`status-historico-${index}`}>
                  <CardComanda
                    titulo={comandaStatus.statusNome}
                    totalComandas={comandaStatus.total}
                    linkTo="/assessorados?tab=1"
                    icon={AssignmentIcon}
                    loading={false}
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
